import { OverlapTransitionStateContext } from "@redotech/react-animation/outlet-transition";
import { OverlapTransitionState } from "@redotech/react-animation/transition";
import { cloneElement, ReactElement, useContext } from "react";
import { createPortal } from "react-dom";
import { RedoHorizontalTabs } from "./arbiter-components/tabs/redo-horizontal-tabs";
import { TabsPortalContext } from "./page";
import { Tabs } from "./tab";
import { RedoHorizontalTabsWithActions } from "./tabs-with-actions";

/**
 * @param tabCount - If specified, the number of tabs in the portal. Will be used to dynamically determine if the portal has tab content.
 * @returns
 */
export function TabsPortal({ children }: { children: ReactElement }) {
  const { portalNode } = useContext(TabsPortalContext);

  const transitionState = useContext(OverlapTransitionStateContext);

  if (!portalNode) {
    return null;
  }

  if (
    !(
      children.type === Tabs ||
      children.type === RedoHorizontalTabs ||
      children.type === RedoHorizontalTabsWithActions
    )
  ) {
    throw new Error(
      "TabsPortal can only render a <Tabs>, <RedoHorizontalTabs>, or <RedoHorizontalTabsWithActions> component.",
    );
  }

  if (transitionState === OverlapTransitionState.EXIT) {
    return null;
  }

  // Portaled tab bars sit inside the page header, which already draws its
  // own full-width bottom border. Force `drawBottomLine={false}` so we don't
  // stack the tab bar's inset border on top of the header's, which used to
  // look like a doubled line. The pageCss `.tabs-wrapper` handles dividers
  // between bars via a full-width pseudo-element when two are stacked.
  const portalChild =
    children.type === RedoHorizontalTabs
      ? cloneElement(children as ReactElement<{ drawBottomLine?: boolean }>, {
          drawBottomLine: false,
        })
      : children;

  return createPortal(portalChild, portalNode);
}
